import { RefObject, useEffect } from 'react';

import { applyMotionVars, getMotionScale, MotionScale } from './getMotionScale';
import { useInViewOnce, UseInViewOnceOptions } from './useInViewOnce';

export type AnimateOnViewResult<T extends HTMLElement> = {
  ref: RefObject<T>;
  isAnimated: boolean;
  /** Spread onto the animated root element. */
  animProps: {
    'data-animate': 'in' | 'ready';
  };
  scale: MotionScale;
};

/**
 * One-shot viewport entrance helper:
 * - attaches a ref
 * - exposes data-animate="ready|in"
 * - syncs responsive CSS motion variables on the node
 */
export const useAnimateOnView = <T extends HTMLElement = HTMLElement>(
  options: UseInViewOnceOptions = {}
): AnimateOnViewResult<T> => {
  const [ref, isAnimated] = useInViewOnce<T>(options);
  const scale = getMotionScale();

  useEffect(() => {
    applyMotionVars(ref.current, getMotionScale());
  }, [ref, isAnimated]);

  return {
    ref,
    isAnimated,
    animProps: {
      'data-animate': isAnimated ? 'in' : 'ready',
    },
    scale,
  };
};
